/**
 * Persist successful compile results (assembly bytes + provenance) to IndexedDB.
 */

import { saveBuild } from "./db";
import { getBuildDeviceInfo } from "./device";
import { getPlatformConfig } from "@/platform/platform-context";
import { resolveIndicatorUrl } from "@/platform/resolve-url";
import type { CompileResult } from "./runtime-types";
import type { CustomIndicatorBuild, CustomIndicatorBuildMeta } from "./types";

export function compileResultToBuildMeta(
  result: CompileResult
): CustomIndicatorBuildMeta {
  return {
    name: result.name,
    version: result.version,
    overlay: result.overlay,
    kind: result.kind,
    className: result.className,
    inputs: result.inputs ?? [],
    plots: result.plots ?? [],
    candleColor: result.candleColor,
    signal: result.signal,
    signals: result.signals,
    zone: result.zone,
    zones: result.zones,
    priceHistogram: result.priceHistogram,
    panes: result.panes,
    dashboard: result.dashboard,
    icon: result.icon,
  };
}

async function fetchRuntimeBuiltAt(): Promise<string | undefined> {
  if (typeof window === "undefined") return undefined;
  try {
    const res = await fetch(
      resolveIndicatorUrl(
        "/indicator-runtime/.runtime-version",
        getPlatformConfig(),
      ),
      { cache: "no-store" },
    );
    if (!res.ok) return undefined;
    const json = (await res.json()) as { builtAt?: string };
    return json.builtAt;
  } catch {
    return undefined;
  }
}

export async function persistBuildFromCompile(
  projectId: string,
  result: CompileResult
): Promise<void> {
  if (!result.success || !result.assemblyBase64) return;

  const assemblyBase64 = result.assemblyBase64;
  const runtimeBuiltAt = await fetchRuntimeBuiltAt();

  const build: CustomIndicatorBuild = {
    projectId,
    assemblyBase64,
    builtAt: Date.now(),
    runtimeBuiltAt,
    device: getBuildDeviceInfo(),
    meta: compileResultToBuildMeta(result),
    sizeBytes: Math.ceil((assemblyBase64.length * 3) / 4),
  };

  await saveBuild(build);
}
